import React, { useState, useEffect } from "react";
import {
  PlusCircle,
  Clock,
  Users,
  Mail,
  Copy,
  Edit,
  Eye,
  Loader2,
  Calendar,
  Bell,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useCreateWorkflow,
  useCloneWorkflow,
  useActivateWorkflow,
  useCommunicationWorkflows,
} from "../../hooks";
import {
  CreateWorkflowRequest,
  Workflow,
  WorkflowsManagerFilterProps,
  WorkflowFilters,
} from "../../types";
import { WorkflowFilterPanel } from "../filters/WorkflowFilterPanel";
import { PAGE_SIZE } from "@shared/utils/constants";
import { logEvent } from "@shared/analytics/analytics";

interface WorkflowsManagerProps {
  statusFilter?: string;
  defaultFilters?: Partial<WorkflowFilters>;
}

export const WorkflowsManager: React.FC<WorkflowsManagerProps> = ({
  statusFilter = "all",
  defaultFilters = {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cloningWorkflow, setCloningWorkflow] = useState<number | null>(null);

  const itemsPerPage = PAGE_SIZE;

  const getFiltersFromUrl = (): WorkflowFilters => {
    const page = parseInt(searchParams.get("page") || "1");

    const urlFilters: WorkflowFilters = {
      page,
      limit: itemsPerPage,
    };

    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      urlFilters.status = statusParam.split(",");
    }

    const triggerTypeParam = searchParams.get("triggerType");
    if (triggerTypeParam) {
      urlFilters.triggerType = triggerTypeParam;
    }

    const recipientTypeParam = searchParams.get("recipientType");
    if (recipientTypeParam) {
      urlFilters.recipientType = recipientTypeParam;
    }

    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.searchData = queryParam;
    }

    return urlFilters;
  };

  const getInitialFilters = (): WorkflowFilters => {
    const urlFilters = getFiltersFromUrl();

    // 🔥 FIX: Only apply statusFilter if there are NO URL parameters at all
    const hasUrlFilters =
      urlFilters.status ||
      urlFilters.triggerType ||
      urlFilters.recipientType ||
      urlFilters.searchData;

    // Only apply the prop-based statusFilter if no URL filters are present
    if (!hasUrlFilters && statusFilter !== "all") {
      urlFilters.status = [statusFilter];
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<WorkflowFilters>(getInitialFilters);

  useEffect(() => {
    const newFilters = getInitialFilters();
    setFilters(newFilters);
  }, [searchParams, statusFilter]);

  const {
    data,
    isLoading: loading,
    refetch,
  } = useCommunicationWorkflows(filters);

  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);

  const { mutate: createWorkflow, isPending: isCreating } = useCreateWorkflow();
  const { mutate: cloneWorkflow, isPending: isCloning } = useCloneWorkflow();
  const { mutate: activateWorkflow, isPending: isActivating } =
    useActivateWorkflow();

  const filteredWorkflows = data?.results || [];

  const handleApplyFilters = (newFilters: Partial<WorkflowFilters>) => {
    const params = new URLSearchParams(searchParams);

    params.delete("status");
    params.delete("triggerType");
    params.delete("recipientType");
    params.delete("page");

    if (newFilters.status && newFilters.status.length > 0) {
      params.set("status", newFilters.status.join(","));
    }
    if (newFilters.triggerType) {
      params.set("triggerType", newFilters.triggerType);
    }
    if (newFilters.recipientType) {
      params.set("recipientType", newFilters.recipientType);
    }

    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    params.delete("status");
    params.delete("triggerType");
    params.delete("recipientType");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  const handleCreate = () => {
    logEvent('Clicked New Workflow');
    navigate("/communication-hub/workflows/create");
  };

  const handleDuplicateWorkflow = (workflow: Workflow) => {
    setCloningWorkflow(workflow.id);

    cloneWorkflow(
      {
        workflowId: workflow.id,
        cloneData: {
          name: `${workflow.name} (Copy)`,
        },
      },
      {
        onSettled: () => {
          setCloningWorkflow(null);
        },
      }
    );
  };
  const handleViewWorkflow = (workflow: Workflow) => {
    navigate(`/communication-hub/workflows/view/${workflow.id}`);
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    navigate(`/communication-hub/workflows/create?mode=edit&id=${workflow.id}`);
  };

  const handleToggleWorkflowStatus = (workflow: Workflow) => {
    activateWorkflow({
      workflowId: workflow.id,
      isActive: !workflow.isActive,
    });
  };

  const getStatusBadge = (workflow: Workflow) => {
    if (workflow.isDraft) {
      return { variant: "outline" as const, text: "Draft", className: "" };
    } else if (workflow.isActive) {
      return {
        variant: "default" as const,
        text: "Active",
        className: "bg-success text-success-foreground",
      };
    } else {
      return {
        variant: "default" as const,
        text: "Inactive",
        className: "bg-warning text-warning-foreground",
      };
    }
  };

  const getActionSummary = (actions: any[]) => {
    if (!actions || actions.length === 0) return "No actions configured";

    const channelCounts: Record<string, number> = {};
    actions.forEach((action) => {
      const channel = action.channelType || "Unknown";
      channelCounts[channel] = (channelCounts[channel] || 0) + 1;
    });

    return Object.entries(channelCounts)
      .map(([channel, count]) => `${count} ${channel}${count > 1 ? "s" : ""}`)
      .join(", ");
  };

  const getPerformanceRate = (workflow: Workflow) => {
    if (workflow.totalNotifications === 0) return "0%";
    return `${workflow.successRate.toFixed(1)}%`;
  };

  const columns: TableColumn<Workflow>[] = [
    {
      key: "name",
      header: "Workflow",
      width: "w-64",
      sortable: true,
      type: "custom",
      render: (_, row: Workflow) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.name}</div>
          <div className="text-sm text-gray-500 line-clamp-2">
            {row.description}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-32",
      sortable: true,
      type: "badge",
      badgeConfig: {
        getVariant: (_, row: Workflow) => getStatusBadge(row).variant,
        getClassName: (_, row: Workflow) => getStatusBadge(row).className,
      },
      render: (_, row: Workflow) => getStatusBadge(row).text,
    },
    {
      key: "triggerType",
      header: "Trigger Type",
      width: "w-40",
      type: "custom",
      render: (_, row: Workflow) => (
        <div className="flex items-center">
          <span className="text-sm">
            {row.triggerType === 1 ? "Time-Based" : "Event-Based"}
          </span>
        </div>
      ),
    },
    {
      key: "recipientType",
      header: "Audience",
      width: "w-32",
      type: "custom",
      render: (_, row: Workflow) => (
        <div className="flex items-center">
          <Users className="h-4 w-4 mr-1" />
          <span className="text-sm">{row.recipientType}</span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions Count",
      width: "w-32",
      type: "custom",
      render: (_, row: Workflow) => (
        <div className="flex items-center">
          <span className="text-sm">
            {row.actions?.length || 0} action
            {(row.actions?.length || 0) !== 1 ? "s" : ""}
          </span>
        </div>
      ),
    },
    {
      key: "channels",
      header: "Channels",
      width: "w-48",
      type: "custom",
      render: (_, row: Workflow) => (
        <div className="flex items-center">
          <span className="text-sm">{getActionSummary(row.actions || [])}</span>
        </div>
      ),
    },
    {
      key: "totalNotifications",
      header: "Total Sent",
      width: "w-28",
      sortable: true,
      type: "custom",
      render: (_, row: Workflow) => (
        <span className="text-sm font-semibold">{row.totalNotifications}</span>
      ),
    },
    {
      key: "deliveredCount",
      header: "Delivered",
      width: "w-28",
      sortable: true,
      type: "custom",
      render: (_, row: Workflow) => (
        <span className="text-sm font-semibold">{row.deliveredCount}</span>
      ),
    },
    {
      key: "successRate",
      header: "Success Rate",
      width: "w-32",
      sortable: true,
      type: "custom",
      render: (_, row: Workflow) => (
        <span className="text-sm font-semibold text-success">
          {getPerformanceRate(row)}
        </span>
      ),
    },
    {
      key: "createdDate",
      header: "Created",
      width: "w-32",
      sortable: true,
      type: "custom",
      render: (_, row: Workflow) => (
        <span className="text-sm text-muted-foreground">{row.createdDate}</span>
      ),
    },
    {
      key: "lastModifiedDate",
      header: "Modified",
      width: "w-32",
      sortable: true,
      type: "custom",
      render: (_, row: Workflow) => (
        <span className="text-sm text-muted-foreground">
          {row.lastModifiedDate}
        </span>
      ),
    },
    {
      key: "tableActions",
      header: "Actions",
      width: "w-48",
      type: "custom",
      render: (_, row: Workflow) => {
        const isWorkflowCloning = cloningWorkflow === row.id;

        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewWorkflow(row)}
              title="View"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDuplicateWorkflow(row)}
              disabled={isWorkflowCloning}
              title="Duplicate"
            >
              {isWorkflowCloning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditWorkflow(row)}
              disabled={row.isDraft == false}
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Switch
              checked={row.isActive}
              onCheckedChange={() => handleToggleWorkflowStatus(row)}
              disabled={isActivating || row.isDraft == true}
              title="Toggle Status"
            />
          </div>
        );
      },
    },
  ];

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    setSearchParams(params);
  };

  const currentPage = parseInt(searchParams.get("page") || "1");
  const totalItems = data?.count || 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const displayedWorkflows = filteredWorkflows;

  return (
    <div className="space-y-4">
      {/* Generic Table */}
      <GenericTable
        data={displayedWorkflows}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search workflows..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        rowKey="id"
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil(totalItems / itemsPerPage),
          totalItems: totalItems,
          itemsPerPage,
          startItem: startIndex + 1,
          endItem: endIndex,
        }}
        onPageChange={handlePageChange}
        filterPanel={
          <WorkflowFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        emptyMessage="No workflows found"
      />
    </div>
  );
};
